import Router, { useRouter } from 'next/router';
import { getLegoUrl } from './config';
import { toast } from 'react-toastify';
import { getQueryParam } from './query';
import logger from './logger';
import { IAuthUser } from '@monofront/business/contexts';
import { useTranslations } from 'next-intl';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import { RoutesEnum } from '@monofront/types';
import { useAuthentication } from '@context/businessContainer';
import { UserCredential, getAdditionalUserInfo } from '@firebase/auth';

/**
 * Returns the input only if it is a safe same-origin relative path. Rejects
 * absolute URLs, protocol-relative URLs (`//evil.com`) and dangerous schemes
 * such as `javascript:` or `data:` to prevent open-redirect and XSS via the
 * `next` query parameter.
 */
const sanitizeNextPath = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  if (value[0] !== '/' || value[1] === '/' || value[1] === '\\') {
    return null;
  }

  return value;
};

export const redirectToLogin = (referrer?: string) => {
  const safeReferrer = sanitizeNextPath(referrer);

  Router.push(
    `/account/login${
      safeReferrer ? `?next=${encodeURIComponent(safeReferrer)}` : ''
    }`,
  );
};

export const useRedirect = () => {
  const tErrors = useTranslations('Errors');
  const { getPath } = useLocalizedRoutes();
  const { isMember, authUser } = useAuthentication();
  const router = useRouter();

  const redirectToHomePageOrReferrer = async (
    user: IAuthUser | null | undefined,
  ): Promise<boolean> => {
    const checkoutUrlParam = getQueryParam('checkout_url');

    if (checkoutUrlParam && user) {
      try {
        const token: string = await user.getIdToken();

        const response = await fetch(getLegoUrl() + '/order/checkout', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ webUrl: checkoutUrlParam }),
        });

        const { checkoutUrl } = await response.json();

        window.location = checkoutUrl;
      } catch (error: any) {
        logger.error(error);

        toast(tErrors('redirectionError'), {
          progressClassName: 'toast-error',
        });
      }
    }

    const nextParam = sanitizeNextPath(getQueryParam('next'));

    if (nextParam && !nextParam.includes(Router.pathname)) {
      return Router.push(nextParam);
    }

    return Router.push('/home');
  };

  const redirectToPaymentPageIfNewUser = async (
    creds: UserCredential,
    queryParams?: Record<string, string>,
  ): Promise<boolean> => {
    if (getAdditionalUserInfo(creds)?.isNewUser) {
      const queryParamsString =
        (queryParams && `?${new URLSearchParams(queryParams).toString()}`) ||
        '';

      return Router.push(
        `${getPath(RoutesEnum.SUBSCRIPTION_PAYMENT)}${queryParamsString}`,
      );
    }

    return redirectToHomePageOrReferrer(creds.user);
  };

  const redirectToPaymentPageIfNewUserFromSM = async (
    creds: UserCredential,
    queryParams?: Record<string, string>,
  ): Promise<boolean> => {
    if (getAdditionalUserInfo(creds)?.isNewUser) {
      const route = creds.user?.email
        ? '/account/register-newsletter-consent'
        : '/account/register-facebook-email';

      const queryParamsString =
        (queryParams && `?${new URLSearchParams(queryParams).toString()}`) ||
        '';

      return Router.push(`${route}${queryParamsString}`);
    }

    return redirectToHomePageOrReferrer(creds.user);
  };

  const redirectToSubscriptionSummary = async () => {
    if (!isMember) {
      return router.push('/account/subscription-summary');
    }

    return redirectToHomePageOrReferrer(authUser);
  };

  return {
    redirectToHomePageOrReferrer,
    redirectToPaymentPageIfNewUser,
    redirectToPaymentPageIfNewUserFromSM,
    redirectToSubscriptionSummary,
  };
};
